import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Alert } from '../../components/ui/Alert'
import { Toast } from '../../components/ui/Toast'
import { SkeletonCard } from '../../components/ui/Skeleton'
import { ImageUploadField } from '../../components/ui/ImageUploadField'
import { Modal } from '../../components/ui/Modal'
import * as categoryService from '../../services/categoryService'
import * as specialOffersService from '../../services/specialOffersService'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

function toDatetimeLocalValue(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromDatetimeLocalValue(local: string): string {
  if (!local) return ''
  const d = new Date(local)
  return d.toISOString()
}

function formatShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export default function SpecialOffersPage() {
  const { currentStoreId: storeId, loading: storeContextLoading } = useCurrentStore()
  const [items, setItems] = useState<specialOffersService.SpecialOfferListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)
  const [offerModalOpen, setOfferModalOpen] = useState(false)
  const [modalOfferId, setModalOfferId] = useState<number | null>(null)
  const [detail, setDetail] = useState<specialOffersService.SpecialOfferDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null)

  const [form, setForm] = useState({
    title: '',
    badge: '',
    subtitle: '',
    heroImage: '',
    discountType: 'percent' as 'percent' | 'fixed',
    discountValue: '10',
    startsAt: '',
    endsAt: '',
  })

  const [categories, setCategories] = useState<categoryService.CategoryRow[]>([])
  const [categoryPick, setCategoryPick] = useState('')

  const isCreateMode = offerModalOpen && modalOfferId == null

  const refreshList = useCallback(async () => {
    if (storeId == null) return
    setListError(null)
    try {
      const { items: rows } = await specialOffersService.listSpecialOffers(storeId)
      setItems(rows)
    } catch (e) {
      setListError(getUserFacingErrorMessage(e, 'Failed to load offers'))
    }
  }, [storeId])

  const closeOfferModal = useCallback(() => {
    setOfferModalOpen(false)
    setModalOfferId(null)
    setDetail(null)
    setSaveError(null)
    setUploadError(null)
    setDetailLoading(false)
    setCategoryPick('')
  }, [])

  const openCreateModal = useCallback(() => {
    const now = new Date()
    const week = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
    setForm({
      title: 'New offer',
      badge: '',
      subtitle: '',
      heroImage: '',
      discountType: 'percent',
      discountValue: '10',
      startsAt: toDatetimeLocalValue(now.toISOString()),
      endsAt: toDatetimeLocalValue(week.toISOString()),
    })
    setModalOfferId(null)
    setDetail(null)
    setSaveError(null)
    setUploadError(null)
    setOfferModalOpen(true)
  }, [])

  const openEditModal = useCallback((id: number) => {
    setSaveError(null)
    setUploadError(null)
    setModalOfferId(id)
    setDetail(null)
    setOfferModalOpen(true)
  }, [])

  useEffect(() => {
    closeOfferModal()
  }, [storeId, closeOfferModal])

  useEffect(() => {
    let cancelled = false
    if (storeId == null) {
      setLoading(false)
      setItems([])
      return
    }
    setLoading(true)
    specialOffersService
      .listSpecialOffers(storeId)
      .then(({ items: rows }) => {
        if (cancelled) return
        setItems(rows)
      })
      .catch((e) => {
        if (!cancelled) setListError(getUserFacingErrorMessage(e, 'Failed to load offers'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId])

  useEffect(() => {
    let cancelled = false
    if (!offerModalOpen || storeId == null || modalOfferId == null) {
      if (!offerModalOpen || modalOfferId == null) {
        setDetailLoading(false)
      }
      return
    }
    setDetailLoading(true)
    specialOffersService
      .getSpecialOffer(storeId, modalOfferId)
      .then((d) => {
        if (cancelled) return
        setDetail(d)
        setForm({
          title: d.title,
          badge: d.badge ?? '',
          subtitle: d.subtitle ?? '',
          heroImage: d.heroImage ?? '',
          discountType: d.discountType,
          discountValue: String(d.discountValue),
          startsAt: toDatetimeLocalValue(d.startsAt),
          endsAt: toDatetimeLocalValue(d.endsAt),
        })
      })
      .catch((e) => {
        if (!cancelled) setSaveError(getUserFacingErrorMessage(e, 'Failed to load offer'))
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [offerModalOpen, storeId, modalOfferId])

  useEffect(() => {
    let cancelled = false
    if (storeId == null) return
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((res) => {
        if (cancelled) return
        const storeCats = res.items.filter((c) => c.source === 'store' && c.id != null)
        setCategories(storeCats)
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })
    return () => {
      cancelled = true
    }
  }, [storeId])

  const handleCreate = async () => {
    if (storeId == null) return
    setSaveError(null)
    setSaving(true)
    try {
      const created = await specialOffersService.createSpecialOffer(storeId, {
        title: form.title.trim() || 'Untitled offer',
        badge: form.badge.trim() || null,
        subtitle: form.subtitle,
        heroImage: form.heroImage.trim() || null,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        startsAt: fromDatetimeLocalValue(form.startsAt),
        endsAt: fromDatetimeLocalValue(form.endsAt),
      })
      setToast({ message: 'Offer created', variant: 'success' })
      setModalOfferId(created.id)
      await refreshList()
    } catch (e) {
      setSaveError(getUserFacingErrorMessage(e, 'Create failed'))
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (storeId == null || modalOfferId == null) return
    setSaveError(null)
    setSaving(true)
    try {
      await specialOffersService.updateSpecialOffer(storeId, modalOfferId, {
        title: form.title.trim(),
        badge: form.badge.trim() || null,
        subtitle: form.subtitle,
        heroImage: form.heroImage.trim() || null,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        startsAt: fromDatetimeLocalValue(form.startsAt),
        endsAt: fromDatetimeLocalValue(form.endsAt),
      })
      setToast({ message: 'Offer saved', variant: 'success' })
      await refreshList()
      const d = await specialOffersService.getSpecialOffer(storeId, modalOfferId)
      setDetail(d)
    } catch (e) {
      setSaveError(getUserFacingErrorMessage(e, 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (storeId == null) return
    if (!window.confirm('Delete this special offer?')) return
    try {
      await specialOffersService.deleteSpecialOffer(storeId, id)
      if (modalOfferId === id) closeOfferModal()
      setToast({ message: 'Offer deleted', variant: 'success' })
      await refreshList()
    } catch (e) {
      setToast({ message: getUserFacingErrorMessage(e, 'Delete failed'), variant: 'error' })
    }
  }

  const addCat = async () => {
    const id = Number(categoryPick)
    if (storeId == null || modalOfferId == null || !Number.isFinite(id)) return
    try {
      await specialOffersService.addOfferCategory(storeId, modalOfferId, id)
      setToast({ message: 'Category rule added', variant: 'success' })
      const d = await specialOffersService.getSpecialOffer(storeId, modalOfferId)
      setDetail(d)
      await refreshList()
    } catch (e) {
      setToast({ message: getUserFacingErrorMessage(e, 'Could not add category'), variant: 'error' })
    }
  }

  const removeCat = async (categoryId: number) => {
    if (storeId == null || modalOfferId == null) return
    try {
      await specialOffersService.removeOfferCategory(storeId, modalOfferId, categoryId)
      const d = await specialOffersService.getSpecialOffer(storeId, modalOfferId)
      setDetail(d)
      await refreshList()
    } catch (e) {
      setToast({ message: getUserFacingErrorMessage(e, 'Could not remove category'), variant: 'error' })
    }
  }

  const showMembership = !isCreateMode && modalOfferId != null && detail != null && !detailLoading

  return (
    <div className="space-y-8">
      {toast ? (
        <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />
      ) : null}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Special offers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Scheduled discounts with product or category membership. Active offers appear on your customer storefront.
          </p>
        </div>
        {storeId != null ? (
          <Button type="button" className="shrink-0" onClick={openCreateModal}>
            New offer
          </Button>
        ) : null}
      </div>

      {listError ? <Alert variant="destructive">{listError}</Alert> : null}

      {!storeContextLoading && storeId == null ? (
        <Alert variant="warning">No store found.</Alert>
      ) : null}

      <Card className="w-full max-w-none">
        <CardHeader
          title="Offers"
          description="All special offers for the current store. Use New offer to create, or Edit to change details and membership."
        />
        <div className="px-6 pb-6">
          {loading ? <SkeletonCard className="h-40" /> : null}
          {!loading && items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No offers yet. Click New offer to add one.</p>
          ) : null}
          {!loading && items.length > 0 ? (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 font-semibold">S/N</th>
                    <th className="px-4 py-3 font-semibold">Title</th>
                    <th className="px-4 py-3 font-semibold">Slug</th>
                    <th className="px-4 py-3 font-semibold">Discount</th>
                    <th className="px-4 py-3 font-semibold">Active window</th>
                    <th className="px-4 py-3 font-semibold text-center">Products</th>
                    <th className="px-4 py-3 font-semibold text-center">Categories</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((o, rowIndex) => (
                    <tr key={o.id} className="border-b border-border/80 last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                      <td className="px-4 py-3">
                        <div className="max-w-[200px] truncate font-medium">{o.title}</div>
                        {o.badge ? (
                          <div className="mt-0.5 truncate text-xs text-muted-foreground">{o.badge}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{o.slug}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {o.discountType === 'percent'
                          ? `${o.discountValue}%`
                          : `${o.discountValue} off`}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        <div>{formatShortDate(o.startsAt)}</div>
                        <div>→ {formatShortDate(o.endsAt)}</div>
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums">{o.explicitProductCount ?? 0}</td>
                      <td className="px-4 py-3 text-center tabular-nums">{o.categoryRuleCount ?? 0}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            className="text-xs"
                            onClick={() => openEditModal(o.id)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            className="text-xs"
                            onClick={() => void handleDelete(o.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Card>

      <Modal
        open={offerModalOpen}
        onClose={closeOfferModal}
        size="xl"
        title={isCreateMode ? 'Create offer' : 'Edit offer'}
        footer={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={closeOfferModal}>
              Close
            </Button>
            {isCreateMode ? (
              <Button type="button" onClick={() => void handleCreate()} disabled={saving || storeId == null}>
                {saving ? 'Creating…' : 'Create'}
              </Button>
            ) : (
              <>
                <Button type="button" onClick={() => void handleSave()} disabled={saving || modalOfferId == null}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                {modalOfferId != null ? (
                  <Button
                    type="button"
                    variant="danger"
                    className="text-xs"
                    onClick={() => void handleDelete(modalOfferId)}
                  >
                    Delete offer
                  </Button>
                ) : null}
              </>
            )}
          </div>
        }
      >
        <div className="max-h-[min(70vh,560px)] space-y-4 overflow-y-auto pr-1">
          {saveError ? <Alert variant="destructive">{saveError}</Alert> : null}
          {uploadError ? <Alert variant="destructive">{uploadError}</Alert> : null}
          {modalOfferId != null && !isCreateMode && detailLoading && !detail ? (
            <SkeletonCard className="h-48" />
          ) : null}

          {(isCreateMode || (modalOfferId != null && detail != null)) && (
            <>
              <Input
                label="Title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
              <Input
                label="Badge (e.g. Save 20%)"
                value={form.badge}
                onChange={(e) => setForm((f) => ({ ...f, badge: e.target.value }))}
              />
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">Subtitle</label>
                <textarea
                  className="w-full min-h-[80px] rounded-2xl border border-input bg-background px-4 py-2.5 text-sm shadow-sm"
                  value={form.subtitle}
                  onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                />
              </div>
              <ImageUploadField
                label="Hero image (slider)"
                hint="Optional. Wide image for the storefront deals carousel."
                multiple={false}
                disabled={saving}
                onUploadStateChange={() => {}}
                onUploadError={(msg) => setUploadError(msg)}
                onUploaded={(urls) => {
                  setUploadError(null)
                  if (urls[0]) setForm((f) => ({ ...f, heroImage: urls[0] }))
                }}
              />
              <Input
                label="Hero image URL (paste or upload above)"
                value={form.heroImage}
                onChange={(e) => setForm((f) => ({ ...f, heroImage: e.target.value }))}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  id="disc-type-modal"
                  label="Discount type"
                  value={form.discountType}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      discountType: e.target.value as 'percent' | 'fixed',
                    }))
                  }
                >
                  <option value="percent">Percent off</option>
                  <option value="fixed">Fixed amount off</option>
                </Select>
                <Input
                  label={form.discountType === 'percent' ? 'Percent (0–100)' : 'Amount off'}
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.discountValue}
                  onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Starts"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                />
                <Input
                  label="Ends"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                />
              </div>
            </>
          )}

          {!isCreateMode && modalOfferId != null && !detailLoading && detail == null ? (
            <p className="text-sm text-muted-foreground">Could not load offer.</p>
          ) : null}

          {showMembership ? (
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-sm font-semibold text-foreground">Membership</h3>
              <p className="text-xs text-muted-foreground">
                Save the offer first if you just created it, then add products or category rules.
              </p>
              <div className="rounded-xl border border-border bg-muted/20 px-3 py-3">
                <p className="text-sm text-foreground">
                  {(detail!.explicitProducts?.length ?? detail!.productIds.length) || 0} product
                  {(detail!.explicitProducts?.length ?? detail!.productIds.length) === 1 ? '' : 's'} in this offer
                </p>
                {storeId != null && modalOfferId != null ? (
                  <Link
                    className="mt-2 inline-block"
                    to={`/special-offers/${modalOfferId}/products?storeId=${encodeURIComponent(String(storeId))}`}
                  >
                    <Button type="button" variant="secondary" className="text-xs">
                      Manage products
                    </Button>
                  </Link>
                ) : null}
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[200px] flex-1">
                  <Select
                    id="pick-cat-modal"
                    label="Add category (all products in category)"
                    value={categoryPick}
                    onChange={(e) => setCategoryPick(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {categories
                      .filter((c) => c.id != null)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.slug})
                        </option>
                      ))}
                  </Select>
                </div>
                <Button type="button" variant="secondary" className="text-xs" onClick={() => void addCat()}>
                  Add rule
                </Button>
              </div>
              {detail!.storeCategoryIds.length ? (
                <ul className="space-y-1 text-sm">
                  {detail!.storeCategoryIds.map((cid) => {
                    const meta = categories.find((c) => c.id === cid)
                    return (
                      <li
                        key={cid}
                        className="flex items-center justify-between rounded-lg border border-border px-2 py-1"
                      >
                        <span>{meta ? `${meta.name} (${meta.slug})` : `Category #${cid}`}</span>
                        <button
                          type="button"
                          className="text-xs text-destructive hover:underline"
                          onClick={() => void removeCat(cid)}
                        >
                          Remove
                        </button>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No category rules.</p>
              )}
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}
